import _get from 'lodash/get';
import React, { useCallback, useMemo, Fragment, useEffect, useState, useContext } from 'react';
import { Button } from 'react-bootstrap';
import ReactSelect, { Props as ReactSelectProps } from 'react-select';
import AsyncReactSelect from 'react-select/async';
import { useField } from 'formik';
import { FormField } from '../../../types/general';
import * as FieldsProps from '../../../types/fields';
import './Select.scss';
import { LocaleContext } from 'src/app/LocaleProvider';

export type Props = FormField;

interface ReactSelectOption {
	value: FieldsProps.SelectOptionValue;
	label: string;
}

/**
 * Dropdown control to select a single option from a list
 */
const Select: React.FC<Props> = (props) => {
	const { locale, locales } = useContext(LocaleContext);
	const { schema } = props;
	const { name, props: fieldProps = {} } = schema;
	const { items, request, multiple, readonly, disabled, creationURL, onRefresh } = fieldProps as FieldsProps.Select;
	const [focus, setFocus] = useState(false);
	const [field, meta, helpers] = useField(name);

	const { value } = field;
	const { setValue } = helpers;

	// methods
	const handleChange = useCallback(
		(selected) => {
			if (selected) {
				setValue(multiple ? selected.map((option: FieldsProps.SelectOption) => option.value) : selected.value);
			} else {
				setValue(undefined);
			}
		},
		[multiple]
	);

	const loadOptions = useCallback((inputValue: string, callback: (options: FieldsProps.SelectOption[]) => void) => {
		setTimeout(() => {
			// eslint-disable-next-line standard/no-callback-literal
			callback([]); // @todo
		}, 1000);
	}, []);

	const handleInputChange = useCallback((newValue: string) => {
		// transform / set states / do actions
		// ...
		return newValue;
	}, []);

	// values
	const options = useMemo<ReactSelectOption[]>(
		() =>
			(items || []).map((i) => {
				// localized option text
				let text = i.text as string;
				if (locale && i.text && typeof i.text !== 'string') {
					const localeText = _get(i, `text.${locale.key}`);
					if (localeText) text = localeText;
				}
				return { value: i.value, label: text };
			}),
		[locale, items]
	);
	const defaultValue = value
		? multiple
			? options.filter((i) => (value || []).includes(i.value))
			: options.find((i) => value === i.value)
		: null;

	const selectProps = {
		defaultValue,
		onChange: handleChange,
		options: options,
		isDisabled: readonly || disabled,
		isClearable: true,
		onFocus: () => setFocus(true),
		onBlur: () => setFocus(false),
	} as Partial<ReactSelectProps>;
	if (multiple) {
		selectProps.isMulti = true;
	}

	if (request) {
		// retrieve options with xhr
		return (
			<div key={`field-${field?.name}`}>
				<AsyncReactSelect
					{...selectProps}
					cacheOptions
					loadOptions={loadOptions}
					defaultOptions
					onInputChange={handleInputChange}
				/>
			</div>
		);
	}
	const currentValue = multiple
		? options.filter((opt) => (value || []).includes(opt.value))
		: options.find((opt) => opt.value === value) || '';
	return (
		<div key={`field-${field?.name}`} className={`Field-select ${focus ? 'focus' : ''}`}>
			<ReactSelect value={currentValue} {...selectProps} />
			<div className='hstack gap-2 mt-1'>
				<div>
					{onRefresh && (
						<Button variant='outline-secondary' size='sm' onClick={onRefresh}>
							<i className='bi bi-arrow-clockwise' /> Aggiorna
						</Button>
					)}
				</div>
				<div className='ms-auto'>
					{creationURL && typeof creationURL === 'function' && creationURL()}
					{creationURL && typeof creationURL === 'string' && (
						<Fragment>
							<Button variant='outline-primary' size='sm' href={creationURL} target='_blank'>
								<i className='bi bi-plus' /> Nuovo
							</Button>
						</Fragment>
					)}
				</div>
			</div>
		</div>
	);
};

export default Select;
